import React, { useState } from 'react'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { pick } from 'ramda'
import { routes } from 'core/utils/routes'
import DocumentMeta from 'core/components/DocumentMeta'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import InferActionParams from 'core/actions/InferActionParams'
import { listIngresses } from 'k8s/components/ingresses/new-actions'
import { ingressesSelector } from 'k8s/components/ingresses/selectors'
import { useSelector } from 'react-redux'
import usePluginRouter from 'core/hooks/usePluginRouter'
import { IIngressDetailsPageTabs } from 'k8s/components/ingresses/model'
import ClusterNameCell from 'k8s/components/common/cells/ClusterNameCell'
import { durationBetweenDates } from 'utils/misc'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import AddIngressModal from './AddIngressModal'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import DeleteIngressDialog from './DeleteIngressDialog'

type ModelDataKey = DataKeys.Ingresses
type SelectorModel = ArrayElement<ReturnType<typeof ingressesSelector>>
type ActionParams = InferActionParams<typeof listIngresses>
//@fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {
  masterNodeClusters: boolean
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId', 'namespace']
const defaultParams: Params & SortingState = {
  clusterId: null,
  namespace: null,
  masterNodeClusters: true,
  healthyClusters: true,
  orderBy: 'name',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'Ingresses',
  listTablePrefs,
)

const searchTargets = ['name', 'clusterName']

export const getIngressesColumns = (pluginId = 'kubernetes'): GridViewColumn<SelectorModel>[] => {
  const routePath = pluginId === 'kubevirt' ? 'kubevirtNetworking' : 'networking'
  return [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ clusterId, id }) =>
          routes[routePath].ingresses.details.path({
            clusterId,
            id,
            tab: IIngressDetailsPageTabs.Overview,
          }),
      }),
    },
    { key: 'clusterName', label: 'Cluster', width: 'medium', CellComponent: ClusterNameCell },
    { key: 'namespace', label: 'Namespace' },
    {
      key: 'created',
      label: 'Age',
      formatFn: (value) => durationBetweenDates({ labels: ['d'] })(value),
    },
    {
      key: 'labels',
      label: 'Labels',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    },
    {
      key: 'annotations',
      label: 'Annotations',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: ': ' }),
    },
  ]
}

export default function IngressesListPage() {
  const { currentPluginId } = usePluginRouter()
  const routePath = currentPluginId === 'kubevirt' ? 'kubevirtNetworking' : 'networking'
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listIngresses, {
    params,
    requiredParams,
  })
  const ingresses = useSelector(ingressesSelector)

  const columns = getIngressesColumns(currentPluginId)

  const [selectedIngress, setSelectedIngress] = useState<SelectorModel>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (ingress) => {
        setSelectedIngress(ingress)
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  return (
    <>
      <DocumentMeta title="Ingresses" />
      <AddIngressModal addRoute={routes[routePath].ingresses.add} />
      {showDeleteDialog && (
        <DeleteIngressDialog onClose={() => setShowDeleteDialog(false)} rows={[selectedIngress]} />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.Ingresses}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={ingresses}
        columns={columns}
        addUrl={routes[routePath].ingresses.add.path()}
        addText="Add Ingresses"
        getParamsUpdater={getParamsUpdater}
        rowMenuItems={rowMenuItems}
        showItemsCountInLabel
        showRowMenuForSingleRowActions
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
